import React from 'react';
import type { LubricantSpec } from '../../types';
import { Modal } from '../common/Modal';
import { Droplet, AlertTriangle, ShieldCheck, Wrench, X } from 'lucide-react';

interface LubricantGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  lubricants?: LubricantSpec[];
  safetyCautions?: string[];
}

export const LubricantGuideModal: React.FC<LubricantGuideModalProps> = ({
  isOpen,
  onClose,
  lubricants = [],
  safetyCautions = [],
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="คู่มือและคำแนะนำใช้น้ำมัน (Lubrication Guide)"
      maxWidth="xl"
    >
      <div className="space-y-6 pr-1">
        {/* Section 1: Safety & Operational Cautions */}
        {safetyCautions.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-amber-800 flex items-center gap-2 uppercase tracking-wide">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              ข้อควรระวังสำคัญด้านความปลอดภัยและการใช้น้ำมัน
            </h3>
            <ul className="space-y-2 text-xs text-amber-950/90 leading-relaxed font-medium">
              {safetyCautions.map((caution, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <span className="font-bold text-amber-700 shrink-0">{idx + 1}.</span>
                  <span>{caution}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Section 2: Approved Lubricants Table */}
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 uppercase tracking-wide">
            <Droplet className="w-5 h-5 text-blue-600 shrink-0" />
            ตารางมาตรฐานสารหล่อลื่นที่ผ่านการรับรอง (Certified Lubricants)
          </h3>

          <div className="space-y-3">
            {lubricants.map((lube) => (
              <div
                key={lube.symbol || lube.code}
                className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 hover:border-slate-300 transition-colors shadow-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                  <div className="flex items-center gap-2.5">
                    <span className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 font-mono font-black text-sm sm:text-base border border-blue-200 shadow-2xs">
                      {lube.symbol || lube.code}
                    </span>
                    <span className="text-sm font-bold text-slate-900">
                      {lube.name || lube.description}
                    </span>
                  </div>
                  <span className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded border border-slate-200 font-medium">
                    {lube.specification}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-slate-500 block font-semibold mb-1 flex items-center gap-1">
                      <Wrench className="w-3.5 h-3.5 text-blue-600" />
                      จุดบำรุงรักษาบนเครื่องจักร:
                    </span>
                    <p className="text-slate-800 pl-4">{lube.points}</p>
                  </div>

                  <div>
                    <span className="text-slate-500 block font-semibold mb-1 flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                      ปริมาณถังและรอบเวลา:
                    </span>
                    <p className="text-slate-800 pl-4">{lube.intervalAndQuantity}</p>
                  </div>
                </div>

                {lube.equivalents && lube.equivalents.length > 0 && (
                  <div className="pt-2 border-t border-slate-100 text-xs">
                    <span className="text-slate-500 font-semibold">แบรนด์ที่เทียบเคียงได้: </span>
                    <span className="text-slate-700 font-medium">
                      {lube.equivalents.join(' • ')}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
